import { inject, Injectable } from '@angular/core';
import { sortArray } from '@lx/shared/utils-array';
import { map, Observable } from 'rxjs';
import { AyaCourse, AyaCourseSummary, GroupedAyaCourse } from '../types';
import { Memoize } from '@lx/shared/utils-http';
import { AyaControllerApiService } from '@lx/shared/api';
import { getLocale } from '@lx/shared/i18n';
import { getCompanyHeader } from '@lx/shared/tokens';

@Injectable({
  providedIn: 'root',
})
export class AyaCourseService {
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();
  private readonly ayaService = inject(AyaControllerApiService);

  /**
   * Loads Aya courses and return them grouped by type.
   * @returns Observable of AyaCourses
   */
  @Memoize({ tags: ['getGroupedCourses'], hashFunction: (languageCode: string) => languageCode })
  getGroupedCourses(languageCode: string) {
    return this.ayaService.findAyaCourses(this.locale, this.companyHeader, languageCode).pipe(
      map((res) => this.groupCourses(res.courses)),
      map(this.sortGroupCourses),
    );
  }

  filterGroupedAyaCoursesByQuery(coursesToFilter$: Observable<GroupedAyaCourse[]>, query: string): Observable<GroupedAyaCourse[]> {
    return coursesToFilter$.pipe(
      map((courses) => {
        if (!query) {
          return courses;
        }

        const lowerQuery = query.toLowerCase();

        return courses
          .map((group) => {
            const groupMatches = group.label.toLowerCase().includes(lowerQuery);

            if (groupMatches) {
              return group;
            }

            const matchingItems = group.items.filter((item) => item.label.toLowerCase().includes(lowerQuery));

            return matchingItems.length > 0 ? { ...group, items: matchingItems } : null;
          })
          .filter((group): group is GroupedAyaCourse => group !== null);
      }),
    );
  }

  getCourseSummary(courseCode: string, languageCode: string): Observable<AyaCourseSummary> {
    return this.ayaService.getAyaSummary(courseCode, this.locale, this.companyHeader, languageCode);
  }

  private sortGroupCourses(groupedCourses: GroupedAyaCourse[]): GroupedAyaCourse[] {
    const sortedCoursesTree = sortArray(groupedCourses, [{ property: 'weight' }, { property: 'label' }]);

    sortedCoursesTree.forEach((courseType) => {
      const sortedCourses = sortArray(courseType.items, [{ property: 'weight' }]);
      courseType.items = [...sortedCourses];
    });

    return sortedCoursesTree;
  }

  private groupCourses(courses: AyaCourse[]): GroupedAyaCourse[] {
    const typeGroups = courses.reduce<Record<string, GroupedAyaCourse>>((groups, course) => {
      const { name: typeName, description, code: typeCode, weight: typeWeight } = course.type;

      const existingGroup = groups[typeName] ?? {
        label: typeName,
        description,
        value: typeCode,
        iconUrl: `/assets/images/aya-${typeCode.toLowerCase().replace(/_/g, '-')}.svg`,
        items: [],
        weight: typeWeight,
      };

      const updatedGroup: GroupedAyaCourse = {
        ...existingGroup,
        items: [...existingGroup.items, { label: course.name, value: course.code, weight: course.weight }],
      };

      return { ...groups, [typeName]: updatedGroup };
    }, {});

    return Object.values(typeGroups);
  }
}
